import clsx, { type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { createTV } from "tailwind-variants"

/**
 * `text-micro` and the `text-display*` sizes are ours (tailwind.config.js). Without
 * telling tailwind-merge they are font sizes it reads them as colours and drops
 * them in favour of the real colour next to them, so a tag meant to be 11px came out
 * at the body size.
 */
const twMergeConfig = {
  extend: {
    classGroups: {
      "font-size": [{ text: ["micro", "display-lg", "display", "display-sm"] }],
    },
  },
}
const twMerge = extendTailwindMerge(twMergeConfig)

/** `tv` for the variants components: tailwind-variants merges with its own tailwind-merge, so it needs the same config. */
export const tv = createTV({ twMergeConfig })

/**
 * The neutral tile: bone fill, navy ink, hairline ring (the kit has one colour of
 * structure, so a survey, a report, a task and a company all wear this and the icon or
 * monogram says which). Use it instead of writing the dark pairing out by hand.
 */
export const neutralTile =
  "bg-gray-100 text-primary-600 ring-gray-300 dark:bg-gray-800 dark:text-gray-50 dark:ring-gray-700"

/** The small uppercase label above a form control. */
export const labelClass =
  "mb-1.5 block text-micro font-medium uppercase text-gray-500 dark:text-gray-400"

export function cx(...args: ClassValue[]) {
  return twMerge(clsx(...args))
}

/**
 * Focus is the brand's most repeated appearance: every button, link and field
 * in the app wears one of these. They were still Tailwind's stock blue after
 * the palette moved, which made the one colour a keyboard user sees most often
 * the only one that was not ours.
 */
export const focusInput = [
  "focus:ring-2 focus:ring-inset",
  "focus:ring-gray-100 focus:dark:ring-gray-800",
  "focus:border-primary-600 focus:dark:border-gray-50",
]

/**
 * `[outline-style:solid]` rather than Tailwind's bare `outline`: this project
 * builds on Tailwind 3, where `outline` means the style, but tailwind-merge 3
 * is written for Tailwind 4, where it means a 1px width. So it read `outline`
 * and `outline-0` as the same property and dropped the first — leaving every
 * ring here with a colour, a width and no style at all. What still appeared
 * was the browser's own `outline: auto` on a focused element, which is why
 * this went unseen: the ring rendered, it just was not ours, and anything
 * that is not itself focusable (a row that delegates focus to a link inside
 * it) got no ring whatsoever. Written as an arbitrary property, it names a
 * property tailwind-merge has nothing to confuse it with.
 */
export const focusRing = [
  "[outline-style:solid] outline-offset-2 outline-0 focus-visible:outline-2",
  "outline-primary-600 dark:outline-gray-50",
]

export const hasErrorInput = [
  "ring-2",
  "border-red-500 dark:border-red-700",
  "ring-red-200 dark:ring-red-700/30",
]
